/**
 * AccountTasksSection — Tasks card + TaskDialog + TaskDetailDialog + ViewAll for AccountOverview.
 *
 * Props: accountId, account, canEditAccount, tasks, allTasks, tasksLoading,
 *        invalidateTasks, users, usersMap, allAssociations, allComms, contactEntries, horses
 */
import React, { useState } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Collapsible, CollapsibleContent } from '../../ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Skeleton } from '../../ui/skeleton';
import { CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { activityAPI } from '../../../services/api';
import { SectionHeader, TaskStatusIndicator } from '../../common/OverviewHelpers';
import { TaskDialog, TaskDetailDialog } from '../ActivityDialogs';

const SectionSkeleton = ({ rows = 3 }) => (
  <div className="space-y-2">
    {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
  </div>
);

const AccountTasksSection = ({
  account, canEditAccount,
  tasks = [], allTasks = [], tasksLoading,
  invalidateTasks,
  users = [], usersMap = {},
  allAssociations = [], allComms = [], contactEntries = [], horses = [],
}) => {
  const [open, setOpen] = useState(true);
  const [searchLocal, setSearchLocal] = useState('');
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskDetailOpen, setTaskDetailOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [allTasksDialogOpen, setAllTasksDialogOpen] = useState(false);
  const [taskSearchTerm, setTaskSearchTerm] = useState('');
  const [taskStatusFilter, setTaskStatusFilter] = useState('all');
  const [taskAssignedFilter, setTaskAssignedFilter] = useState('all');

  const handleMarkComplete = async (taskId, e) => {
    e.stopPropagation();
    try {
      await activityAPI.update(taskId, { task_status: 'completed' });
      toast.success('Task marked as complete');
      invalidateTasks?.();
    } catch {
      toast.error('Failed to update task');
    }
  };

  const filtered = tasks.filter(t => {
    if (!searchLocal) return true;
    const s = searchLocal.toLowerCase();
    const dueDateStr = t.end_date_time ? new Date(t.end_date_time).toLocaleDateString() : '';
    return t.activity_name?.toLowerCase().includes(s) ||
           t.description?.toLowerCase().includes(s) ||
           t.task_status?.toLowerCase().includes(s) ||
           usersMap[t.assigned_to_user_id]?.toLowerCase().includes(s) ||
           dueDateStr.toLowerCase().includes(s);
  });

  const filteredAll = allTasks.filter(t => {
    const searchMatch = taskSearchTerm === '' ||
      t.activity_name?.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
      t.description?.toLowerCase().includes(taskSearchTerm.toLowerCase());
    const statusMatch = taskStatusFilter === 'all' || t.task_status === taskStatusFilter;
    const assignedMatch = taskAssignedFilter === 'all' ||
      (taskAssignedFilter === 'unassigned' && !t.assigned_to_user_id) ||
      t.assigned_to_user_id === taskAssignedFilter;
    return searchMatch && statusMatch && assignedMatch;
  }).sort((a, b) => {
    if (!a.end_date_time && !b.end_date_time) return 0;
    if (!a.end_date_time) return 1;
    if (!b.end_date_time) return -1;
    return new Date(a.end_date_time) - new Date(b.end_date_time);
  });

  return (
    <>
      <Card>
        <CardContent className="pt-6">
          <Collapsible open={open} onOpenChange={setOpen}>
            <SectionHeader
              title="Tasks"
              icon={CheckCircle2}
              isOpen={open}
              onToggle={() => setOpen(o => !o)}
              action={canEditAccount ? () => setTaskDialogOpen(true) : undefined}
            />
            <CollapsibleContent className="mt-4">
              <div className="mb-3">
                <Input
                  placeholder="Search tasks..."
                  value={searchLocal}
                  onChange={e => setSearchLocal(e.target.value)}
                  className="w-full"
                />
              </div>
              {tasksLoading && <SectionSkeleton rows={3} />}
              {!tasksLoading && (
                filtered.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No tasks found.</p>
                ) : (
                  <div className="space-y-2">
                    {filtered.map(task => {
                      const isOverdue = task.end_date_time && new Date(task.end_date_time) < new Date() &&
                        task.task_status !== 'completed' && task.task_status !== 'cancelled';
                      return (
                        <div
                          key={task.id}
                          className={`flex items-start gap-3 p-3 border rounded hover:bg-muted/50 cursor-pointer group ${isOverdue ? 'border-red-500' : ''}`}
                          onClick={() => { setSelectedTask(task); setTaskDetailOpen(true); }}
                          data-testid={`task-${task.id}`}
                        >
                          <div
                            className={task.task_status === 'completed' || task.task_status === 'cancelled'
                              ? 'mt-0.5 flex-shrink-0'
                              : 'mt-0.5 cursor-pointer hover:scale-110 transition-transform'}
                            onClick={task.task_status === 'completed' || task.task_status === 'cancelled'
                              ? undefined
                              : e => handleMarkComplete(task.id, e)}
                          >
                            {task.task_status === 'completed' || task.task_status === 'cancelled' ? (
                              <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-green-500">
                                <circle cx="12" cy="12" r="10" fill="currentColor" />
                                <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-gray-400">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.3" />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between items-start">
                              <p className="font-medium text-sm">{task.activity_name}</p>
                              <TaskStatusIndicator status={task.task_status} size="md" />
                            </div>
                            {task.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{task.description}</p>}
                            <p className="text-xs text-muted-foreground mt-1">
                              Due: {task.end_date_time ? new Date(task.end_date_time).toLocaleDateString() : 'None'}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              )}
              {!tasksLoading && (
                <div className="pt-3 border-t mt-3">
                  <Button variant="outline" size="sm" className="w-full" onClick={() => setAllTasksDialogOpen(true)}>
                    View All Tasks ({allTasks.length})
                  </Button>
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        accountId={account?.id}
        associations={allAssociations}
        users={users}
        communications={allComms}
        contactEntries={contactEntries}
        horses={horses}
        onSuccess={invalidateTasks}
      />

      <TaskDetailDialog
        open={taskDetailOpen}
        onOpenChange={setTaskDetailOpen}
        task={selectedTask}
        associations={allAssociations}
        users={users}
        usersMap={usersMap}
        communications={allComms}
        contactEntries={contactEntries}
        horses={horses}
        onSuccess={invalidateTasks}
        onTaskUpdate={updated => { setSelectedTask(updated); invalidateTasks?.(); }}
        canEditAccount={canEditAccount}
      />

      {/* View All Tasks Dialog */}
      <Dialog open={allTasksDialogOpen} onOpenChange={setAllTasksDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle>All Tasks</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Search by name or notes..."
              value={taskSearchTerm}
              onChange={e => setTaskSearchTerm(e.target.value)}
              className="w-full"
            />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground mb-1">Status</Label>
                <Select value={taskStatusFilter} onValueChange={setTaskStatusFilter}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1">Assigned To</Label>
                <Select value={taskAssignedFilter} onValueChange={setTaskAssignedFilter}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto space-y-2">
            {filteredAll.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No tasks found.</p>
            ) : filteredAll.map(task => {
              const isOverdue = task.end_date_time && new Date(task.end_date_time) < new Date() &&
                task.task_status !== 'completed' && task.task_status !== 'cancelled';
              return (
                <div
                  key={task.id}
                  className={`flex items-start gap-3 p-3 border rounded hover:bg-muted/50 cursor-pointer ${isOverdue ? 'border-red-500' : ''}`}
                  onClick={() => { setSelectedTask(task); setAllTasksDialogOpen(false); setTaskDetailOpen(true); }}
                >
                  <div
                    className={task.task_status === 'completed' || task.task_status === 'cancelled'
                      ? 'mt-0.5 flex-shrink-0'
                      : 'mt-0.5 cursor-pointer hover:scale-110 transition-transform'}
                    onClick={task.task_status === 'completed' || task.task_status === 'cancelled'
                      ? undefined
                      : e => handleMarkComplete(task.id, e)}
                  >
                    {task.task_status === 'completed' || task.task_status === 'cancelled' ? (
                      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-green-500">
                        <circle cx="12" cy="12" r="10" fill="currentColor" />
                        <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-gray-400">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.3" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <p className="font-medium text-sm">{task.activity_name}</p>
                      <TaskStatusIndicator status={task.task_status} size="md" />
                    </div>
                    {task.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{task.description}</p>}
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      <span>Due: {task.end_date_time ? new Date(task.end_date_time).toLocaleDateString() : 'None'}</span>
                      {task.assigned_to_user_id && <span>Assigned: {usersMap[task.assigned_to_user_id] || 'Unknown'}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAllTasksDialogOpen(false); setTaskSearchTerm(''); setTaskStatusFilter('all'); setTaskAssignedFilter('all'); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AccountTasksSection;
